/**
 * Parses FAQ pairs from admin HTML — looser variant for offer pages.
 *
 * Differences vs `parseFaqFromHtml`:
 * - Accepts `<h2>` or `<h3>` as question header (admin editors mix both)
 * - Question must end with `?` (filters out section titles like "Sobre a Amazon")
 * - Answer aggregates all `<p>` blocks between this header and the next header
 * - Deduplicates questions by case-insensitive normalized text (admins occasionally
 *   leave the same question in both `<h2>` and `<h3>` after edits)
 *
 * Used only in offer pages (`/promocoes/loja/{slug}/`). Coupon pages keep the
 * stricter `parseFaqFromHtml` to avoid regressing the existing FAQ rendering.
 *
 * @param {string} html - Raw HTML from `store.offerPageDescription`
 * @returns {Array<{ question: string, answer: string }>}
 */
const collectFaqSegments = (html) => {
  const headerRegex = /<(h[23])[^>]*>(.*?)<\/\1>/gis;
  const headers = [];
  let match;

  while ((match = headerRegex.exec(html)) !== null) {
    headers.push({
      rawQuestion: match[2],
      headerStart: match.index,
      headerEnd: match.index + match[0].length,
    });
  }

  const segments = [];

  for (let i = 0; i < headers.length; i += 1) {
    const header = headers[i];
    const question = header.rawQuestion
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\s+([?!.,;:])/g, '$1')
      .trim();

    if (!question.endsWith('?')) continue;

    const next = headers[i + 1];
    const lookahead = html.slice(
      header.headerEnd,
      next ? next.headerStart : html.length
    );

    const paragraphs = [];
    const pRegex = /<p[^>]*>(.*?)<\/p>/gis;
    let pMatch;
    let lastPEnd = 0;

    while ((pMatch = pRegex.exec(lookahead)) !== null) {
      const text = pMatch[1]
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/\s+([?!.,;:])/g, '$1')
        .trim();

      if (text) paragraphs.push(text);
      lastPEnd = pMatch.index + pMatch[0].length;
    }

    if (paragraphs.length === 0) continue;

    segments.push({
      question,
      answer: paragraphs.join(' '),
      blockStart: header.headerStart,
      blockEnd: header.headerEnd + lastPEnd,
    });
  }

  return segments;
};

export default function parseFaqFromHtmlV2(html) {
  if (!html || typeof html !== 'string') return [];

  try {
    const segments = collectFaqSegments(html);

    const result = [];
    const seen = new Set();

    for (const segment of segments) {
      const key = segment.question.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      result.push({
        question: segment.question,
        answer: segment.answer,
      });
    }

    return result;
  } catch {
    return [];
  }
}

/**
 * Removes FAQ blocks (header + one or more <p>s) from `html` so they don't
 * render twice — once in the FaqSection accordion and again in the
 * page-description body. Mirrors `parseFaqFromHtmlV2` extraction rules.
 *
 * Also cleans up orphan section headers (e.g., "Perguntas frequentes:")
 * that wrapped the extracted Q&A and would otherwise stay behind with no
 * content beneath them.
 *
 * @param {string} html - Raw HTML from `store.offerPageDescription`
 * @returns {string} HTML with FAQ blocks stripped
 */
export function stripFaqBlocksV2(html) {
  if (!html || typeof html !== 'string') return html;

  try {
    const segments = collectFaqSegments(html);

    let result = html;

    if (segments.length > 0) {
      result = '';
      let cursor = 0;

      for (const segment of segments) {
        result += html.slice(cursor, segment.blockStart);
        cursor = segment.blockEnd;
      }

      result += html.slice(cursor);
    }

    // Remove orphan h2/h3 headers — those followed only by whitespace and
    // another header, or by end of content. Run iteratively until stable to
    // catch chains (e.g., "Perguntas frequentes:" right before another
    // section header that itself became orphan).
    // Note: `[^<]|<(?!\/\1>)` prevents the inner match from crossing the
    // closing tag of the same header. With plain `.*?` the engine would
    // expand greedily past intermediate `</h2>` to satisfy the lookahead,
    // wrongly stripping headers that DO have content beneath them.
    const orphanHeaderRegex =
      /<(h[23])[^>]*>(?:[^<]|<(?!\/\1>))*?<\/\1>(\s|<br\s*\/?>|<p>\s*<\/p>)*(?=<h[1-6][^>]*>|$)/gis;
    let previous;
    do {
      previous = result;
      result = result.replace(orphanHeaderRegex, '');
    } while (result !== previous);

    return result;
  } catch {
    return html;
  }
}
